(() => {
  const PDFJS_SRC = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.min.mjs';
  const PAGEFLIP_SRC = 'https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.min.js';
  const PDFJS_WORKER = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs';

  const scriptCache = new Map();
  const pdfjsPromise = import(PDFJS_SRC).then((pdfjsLib) => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    return pdfjsLib;
  });

  function loadScript(src) {
    if (scriptCache.has(src)) return scriptCache.get(src);
    const promise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        if (existing.dataset.loaded === 'true') resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
    scriptCache.set(src, promise);
    return promise;
  }

  function setText(root, selector, text) {
    const el = root.querySelector(selector);
    if (el) el.textContent = text;
  }

  function computeSize(root, firstPageViewport) {
    const stage = root.querySelector('[data-bst-pdf-stage]');
    const stageWidth = Math.max(280, (stage?.clientWidth || root.clientWidth || 900) - 28);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const pageRatio = firstPageViewport.width / firstPageViewport.height;
    const spreadPages = isMobile ? 1 : 2;
    const maxPageWidth = isMobile ? stageWidth : Math.floor(stageWidth / 2) - 8;
    const maxPageHeight = Math.max(420, Math.min(window.innerHeight * (isMobile ? 0.68 : 0.72), 780));
    let pageWidth = maxPageWidth;
    let pageHeight = pageWidth / pageRatio;
    if (pageHeight > maxPageHeight) {
      pageHeight = maxPageHeight;
      pageWidth = pageHeight * pageRatio;
    }
    return {
      width: Math.max(240, Math.floor(pageWidth)),
      height: Math.max(340, Math.floor(pageHeight)),
      usePortrait: isMobile || spreadPages === 1,
    };
  }

  async function initFlipbook(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const pdfUrl = root.dataset.pdfUrl;
    const bookEl = root.querySelector('[data-bst-pdf-book]');
    const loader = root.querySelector('[data-bst-pdf-loader]');
    const errorEl = root.querySelector('[data-bst-pdf-error]');
    const prev = root.querySelector('[data-bst-pdf-prev]');
    const next = root.querySelector('[data-bst-pdf-next]');
    const fullscreen = root.querySelector('[data-bst-pdf-fullscreen]');
    const download = root.querySelector('[data-bst-pdf-download]');

    if (!pdfUrl || !bookEl) return;
    if (download) download.href = pdfUrl;

    try {
      const [pdfjsLib] = await Promise.all([pdfjsPromise, loadScript(PAGEFLIP_SRC)]);
      const pdf = await pdfjsLib.getDocument({ url: pdfUrl, withCredentials: false }).promise;
      const firstPage = await pdf.getPage(1);
      const firstViewport = firstPage.getViewport({ scale: 1 });
      const size = computeSize(root, firstViewport);

      bookEl.innerHTML = '';
      const pages = [];
      for (let i = 1; i <= pdf.numPages; i += 1) {
        const page = document.createElement('div');
        page.className = 'bst-pdf-flipbook__page';
        page.dataset.pageNumber = String(i);
        page.setAttribute('aria-label', `Seite ${i}`);
        bookEl.appendChild(page);
        pages.push(page);
      }

      const PageFlip = window.St?.PageFlip;
      if (!PageFlip) throw new Error('PageFlip konnte nicht geladen werden.');

      const pageFlip = new PageFlip(bookEl, {
        width: size.width,
        height: size.height,
        size: 'fixed',
        minWidth: 240,
        maxWidth: size.width,
        minHeight: 340,
        maxHeight: size.height,
        showCover: true,
        usePortrait: size.usePortrait,
        mobileScrollSupport: true,
        flippingTime: 650,
        maxShadowOpacity: 0.25,
      });

      const rendered = new Set();
      async function renderPage(pageNumber) {
        if (pageNumber < 1 || pageNumber > pdf.numPages || rendered.has(pageNumber)) return;
        const target = pages[pageNumber - 1];
        if (!target) return;
        rendered.add(pageNumber);
        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: Math.min(2, window.devicePixelRatio || 1.5) });
        const ratio = viewport.width / viewport.height;
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(size.height * ratio * Math.min(2, window.devicePixelRatio || 1.5));
        canvas.height = Math.floor(size.height * Math.min(2, window.devicePixelRatio || 1.5));
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        target.appendChild(canvas);
        const renderViewport = page.getViewport({ scale: canvas.width / page.getViewport({ scale: 1 }).width });
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: renderViewport }).promise;
      }

      function renderAround(pageIndex) {
        const current = pageIndex + 1;
        for (let p = current - 2; p <= current + 4; p += 1) renderPage(p);
      }

      pageFlip.loadFromHTML(pages);
      loader?.remove();
      renderAround(0);
      setText(root, '[data-bst-pdf-status]', `Seite 1 / ${pdf.numPages}`);

      pageFlip.on('flip', (event) => {
        const pageIndex = event.data;
        renderAround(pageIndex);
        setText(root, '[data-bst-pdf-status]', `Seite ${pageIndex + 1} / ${pdf.numPages}`);
      });

      prev?.addEventListener('click', () => pageFlip.flipPrev());
      next?.addEventListener('click', () => pageFlip.flipNext());
      fullscreen?.addEventListener('click', () => {
        const target = root.querySelector('[data-bst-pdf-shell]') || root;
        if (document.fullscreenElement) document.exitFullscreen?.();
        else target.requestFullscreen?.();
      });
    } catch (error) {
      console.error('[bst-pdf-flipbook]', error);
      loader?.remove();
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = 'Der Online-Katalog konnte nicht geladen werden. Bitte öffnen Sie das PDF über den Download-Link.';
      }
    }
  }

  function initAll() {
    document.querySelectorAll('[data-bst-pdf-flipbook]').forEach(initFlipbook);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
